/**
 * Lib.Common: v-label directive
 *
 * @file
 * Generates a tooltip on hover or click, depending on pointing device and modifiers. If the primary pointing device is
 * a mouse, the default is to display the tooltip on hover. Otherwise, the default is to display the tooltip on click.
 * The tooltip message supports Markdown formatting.
 *
 * Only one tooltip can be visible in the window at a time. Showing a new tooltip will hide the previous one, unless
 * the previous one is marked `persistent`, in which case the new tooltip will be suppressed.
 *
 * For most cases, you will specify the argument as a Markdown string. However, you can also specify it as an object:
 * - msg: A Markdown string containing the text to display.
 * - dismiss: A method that is called on dismiss if this is a persistent tooltip with a dismiss button.
 * - back: A method that, if specified, will cause a "back" button to be displayed that calls it on click.
 * - next: A method that, if specified, will cause a "next" button to be displayed that calls it on click.
 * - done: A method that, if specified, will cause a "done" button to be displayed that calls it on click.
 *
 * @modifier persistent - Show the tooltip immediately, and keep it visible until the element is no longer visible.
 * @modifier dismiss - If persistent, show a dismiss (X) button that can be used to dismiss the tooltip.
 * @modifier click - Show the tooltip on click rather than hover, regardless of pointing device.
 * @modifier hover - Show the tooltip on hover rather than click, regardless of pointing device.
 * @modifier important - Use the `important` background color.
 * @modifier alert - Use the `alert` background color.
 * @modifier new - Use the `new` background color.
 * @modifier accent - Use the `accent` background color.
 * @modifier ok - Use the `ok` background color.
 * @modifier warning - Use the `warning` background color.
 * @modifier error - Use the `error` background color.
 * @modifier buy - Use the `buy` background color.
 * @modifier hold - Use the `hold` background color.
 * @modifier sell - Use the `sell` background color.
 * @modifier pill - Use the `pill` appearance, with completely rounded ends.
 * @modifier top - Display the tooltip above the element, space permitting.
 * @modifier bottom - Display the tooltip below the element, space permitting.
 * @modifier left - Display the tooltip left of the element, space permitting.
 * @modifier right - Display the tooltip right of the element, space permitting.
 * @modifier start - Justify multiline tooltip content at the inline start.
 * @modifier center - Justify multiline tooltip content at center.
 * @modifier end - Justify multiline tooltip content at the inline end.
 *
 * @value {string} - The string to render, with optional Markdown formatting.
 *
 * @example
 * <template lang="pug">
 * span(v-label.accent.persistent.dismiss="$t('This is a persistent tooltip.')") This is some anchor text.
 * </template>
 *
 * @example
 * <template lang="pug">
 * span(v-label="$t('This is a tooltip.')") This is some more anchor text. You'll need to close the above tooltip before hovering on it.
 * </template>
 */


import $device from '../global/device';
import $markdown from '../global/markdown';
import $tooltip from '../global/tooltip';
import { persistent } from '../global/tooltip.vue';

let timeout = 0;


function open() {
    const label = this._label;
    if (label && label.msg != null && !label.suppress && !persistent) {
        if (timeout) {
            clearTimeout(timeout);
            timeout = 0;
        }
        label.ctx.$tooltip(label.msg, { ...label, anchor: this });
    }
}


function openStop(e) {
    open.call(this);
    e.stopPropagation();
}


function close() {
    timeout = setTimeout(() => {
        $tooltip.close(this);
        timeout = 0;
    }, 500);
}


function bind(el, binding, vnode) {
    const update = !!el._label;
    const mod = binding.modifiers;
    const value = binding.value;
    const object = value && typeof value == 'object';
    const msg = object ? value.msg : value;

    el._label = {
        ctx: vnode.context,
        msg,
        suppress: false,
        tone:
            mod.alert       ? 'alert' :
            mod['new']      ? 'new' :
            mod.accent      ? 'accent' :
            mod.buy         ? 'buy' :
            mod.hold        ? 'hold' :
            mod.sell        ? 'sell' :
            mod.ok          ? 'ok' :
            mod.warning     ? 'warning' :
            mod.error       ? 'error' :
            mod.important   ? 'important' : void 0,
        appearance:
            mod.pill        ? 'pill' : void 0,
        position:
            mod.top         ? 'top' :
            mod.bottom      ? 'bottom' :
            mod.left        ? 'left' :
            mod.right       ? 'right' : void 0,
        align:
            mod.start       ? 'start' :
            mod.center      ? 'center' :
            mod.end         ? 'end' : void 0,
        persistent:
            mod.persistent,
        dismiss:
            mod.dismiss ? () => {
                el._label.suppress = true;
                object && value.dismiss && value.dismiss();
            } : void 0,
        onback: object ? value.back : void 0,
        onnext: object ? value.next : void 0,
        ondone: object ? value.done : void 0
    };

    if (!update) {
        if (mod.click || (!mod.hover && !$device.mouse)) {
            el.addEventListener('click', mod.stop ? openStop : open);
        } else if (mod.hover || !mod.open) {
            el.addEventListener('mouseenter', open);
            if (!mod.persistent) {
                el.addEventListener('mouseleave', close);
            }
        }
    }

    if (msg == null) {
        unbind(el);
    } else if (mod.persistent) {
        setTimeout(() => {
            open.call(el);
        }, 30);
    }
}


function unbind(el) {
    if (el._tt) {
        el._tt.close();
    }
}


export default {
    name: 'label',
    directive: {
        bind,
        unbind,
        update: bind
    }
};
